import { useEffect, useState, useRef } from "react";

let _boltSeq = 0;

/**
 * Reusable Lightning-Bolt loader overlay with live percentage counter.
 * Matches the previous all-screen loader visual (teal gradient-fill bolt + % badge),
 * but scoped to any position:relative container.
 *
 * @param {number} [progress] - 0-100 percentage. If omitted, ticks smoothly from 15% to ~95%.
 * @param {string} [label] - Status text below the percentage.
 * @param {number|string} [minHeight] - Optional container minimum height fallback.
 */
const BoltLoader = ({
  progress,
  label = "Loading data\u2026",
  minHeight = 220,
}) => {
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = `bolt-grad-${++_boltSeq}`;
  }
  const id = idRef.current;

  // Auto-ticker for percentage if progress is not passed as an explicit number
  const [internalProgress, setInternalProgress] = useState(() =>
    typeof progress === "number" ? progress : 15
  );

  useEffect(() => {
    if (typeof progress === "number") {
      setInternalProgress(progress);
      return;
    }

    const interval = setInterval(() => {
      setInternalProgress((prev) => {
        if (prev < 80) {
          return prev + Math.floor(Math.random() * 4 + 2);
        }
        if (prev < 94) {
          return prev + 0.5;
        }
        return prev;
      });
    }, 120);

    return () => clearInterval(interval);
  }, [progress]);

  const rawPct = typeof progress === "number" ? progress : internalProgress;
  const pct = Math.min(100, Math.max(5, rawPct));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 15,
        backgroundColor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "inherit",
        minHeight,
        pointerEvents: "none",
      }}
      role="status"
      aria-live="polite"
    >
      {/* Lightning bolt with animated pulse glow */}
      <div className="historical-lightning-loader" aria-hidden="true">
        <svg
          viewBox="0 0 160 247"
          focusable="false"
          style={{ width: 68, height: 110 }}
        >
          <defs>
            <linearGradient
              id={id}
              x1="80"
              y1="241.5"
              x2="80"
              y2="5.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset={`${pct}%`} stopColor="#009F8D" stopOpacity="1" />
              <stop offset={`${pct}%`} stopColor="#009F8D" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#009F8D" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            className="historical-bolt-shape"
            fill={`url(#${id})`}
            stroke="#009F8D"
            strokeWidth="8"
            strokeLinejoin="round"
            d="M137.641 5.5H60.1789L5.5 135.194H67.9251L32.8394 241.5L154.5 86.718H98.9098L137.641 5.5Z"
          />
        </svg>
      </div>

      {/* Percentage number and label */}
      <div className="historical-loader-percent-badge">
        <span className="historical-loader-percent-number">
          {Math.round(pct)}%
        </span>
        <span className="historical-loader-percent-label">{label}</span>
      </div>
    </div>
  );
};

export default BoltLoader;
